import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHelp } from './HelpContext';
import { HELP_GROUPS, HELP_TOPIC_INDEX, HELP_TOPICS } from './content';
import type { HelpFormKey } from './types';
import {
  CreateWorkForm,
  ProposalForm,
  EstimateForm,
  DesignForm,
  AdminSanctionForm,
} from '../components/forms';
import ContractorForm from '../components/forms/ContractorForm';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../components/common/wmsPopupHeader';

export default function HelpDrawer() {
  const { isOpen, topicId, anchor, closeHelp, setTopic } = useHelp();
  const [search, setSearch] = useState('');
  const contentRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Form launched from help. While non-null, the drawer lowers its z-index so
  // the form (z-9999) renders on top.
  const [launchedForm, setLaunchedForm] = useState<HelpFormKey | null>(null);
  const closeLaunchedForm = () => setLaunchedForm(null);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeHelp(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeHelp]);

  // Scroll selected anchor into view when topic / anchor changes
  useEffect(() => {
    if (!isOpen || !topicId) return;
    const t = window.setTimeout(() => {
      const el = contentRef.current;
      if (!el) return;
      if (anchor) {
        const target = el.querySelector<HTMLElement>(`[data-anchor="${CSS.escape(anchor)}"]`);
        if (target) { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      }
      el.scrollTo({ top: 0 });
    }, 60);
    return () => window.clearTimeout(t);
  }, [isOpen, topicId, anchor]);

  const activeTopic = topicId ? HELP_TOPIC_INDEX[topicId] : null;

  const filteredTopicIds = useMemo(() => {
    const raw = search.trim().toLowerCase();
    if (!raw) return null;
    // Split into tokens so the order of words doesn't matter ("work create" ≡ "create work").
    const tokens = raw.split(/\s+/).filter(Boolean);
    return new Set(HELP_TOPICS.filter((t) => {
      const hay = [
        t.title,
        t.summary || '',
        t.id,
        ...t.sections.map((s) => s.title)
      ].join(' ').toLowerCase();
      return tokens.every((tok) => hay.includes(tok));
    }).map((t) => t.id));
  }, [search]);

  // Note: we deliberately do NOT early-return when !isOpen, because a launched
  // form may still be open even after the user closes the help drawer.
  return createPortal(
    <>
    {isOpen && (
    <div
      className={`fixed inset-0 flex ${launchedForm ? 'z-[9980]' : 'z-[10000]'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Help">
      <div className="fixed inset-0 bg-black/40" onClick={closeHelp}></div>
      <div className="relative ml-auto h-full w-full max-w-5xl bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className={WMS_POPUP_HEADER}>
          <div className="flex items-center gap-2 min-w-0">
            <div className={WMS_POPUP_ICON}>
              <i className="ri-question-line" />
            </div>
            <div className="min-w-0">
              <h2 className={WMS_POPUP_TITLE}>Works Management — Help</h2>
              <p className={WMS_POPUP_SUBTITLE}>{activeTopic?.title || 'Pick a topic to get started'}</p>
            </div>
          </div>
          <button onClick={closeHelp} className={WMS_POPUP_CLOSE} aria-label="Close help">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* TOC */}
          <aside className="w-72 border-r bg-gray-50 flex flex-col">
            <div className="p-3 border-b">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Search topics..."
                />
              </div>
            </div>
            <div className="flex-1 overflow-auto py-2">
              {HELP_GROUPS.map((group) => {
                const visibleIds = group.topicIds.filter((id) => !filteredTopicIds || filteredTopicIds.has(id));
                if (visibleIds.length === 0) return null;
                return (
                  <div key={group.id} className="mb-3">
                    <div className="px-3 py-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      <i className={group.icon || 'ri-bookmark-line'}></i>
                      {group.title}
                    </div>
                    <ul>
                      {visibleIds.map((id) => {
                        const t = HELP_TOPIC_INDEX[id];
                        if (!t) return null;
                        const active = topicId === id;
                        return (
                          <li key={id}>
                            <button
                              onClick={() => setTopic(id)}
                              aria-current={active ? 'true' : undefined}
                              className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 border-l-4 transition-all ${
                                active
                                  ? 'bg-gradient-to-r from-indigo-100 to-indigo-50 border-indigo-600 text-indigo-900 shadow-sm ring-1 ring-inset ring-indigo-200'
                                  : 'border-transparent hover:bg-white hover:border-indigo-200 text-gray-700'
                              }`}
                            >
                              <i className={`${t.icon || 'ri-file-text-line'} mt-0.5 ${active ? 'text-indigo-700' : 'text-gray-400'}`}></i>
                              <span>
                                <span className={`block leading-tight ${active ? 'font-semibold text-indigo-900' : 'font-medium'}`}>{t.title}</span>
                                {t.summary && (
                                  <span className={`text-[11px] block leading-snug mt-0.5 ${active ? 'text-indigo-700/80' : 'text-gray-500'}`}>
                                    {t.summary}
                                  </span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
              {filteredTopicIds && filteredTopicIds.size === 0 && (
                <div className="px-4 py-6 text-center text-xs text-gray-500">
                  No topics match "{search}".
                </div>
              )}
            </div>
          </aside>

          {/* Content */}
          <main ref={contentRef} className="flex-1 overflow-auto bg-white">
            {!activeTopic ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                <div className="text-center">
                  <i className="ri-book-open-line text-5xl mb-3 block opacity-40"></i>
                  <p>Select a topic from the left to see the guide.</p>
                </div>
              </div>
            ) : (
              <article className="max-w-3xl mx-auto px-6 py-6">
                <header className="mb-4 pb-3 border-b">
                  <div className="flex items-center gap-3">
                    <i className={`${activeTopic.icon || 'ri-file-text-line'} text-2xl text-indigo-600`}></i>
                    <h1 className="text-xl font-semibold text-gray-900">{activeTopic.title}</h1>
                  </div>
                  {activeTopic.summary && <p className="text-sm text-gray-600 mt-1.5">{activeTopic.summary}</p>}
                  {activeTopic.pageLinks && activeTopic.pageLinks.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {activeTopic.pageLinks.map((link) => {
                        const onThisPage = location.pathname === link.to;
                        return (
                          <button
                            key={link.to}
                            type="button"
                            onClick={() => { if (!onThisPage) navigate(link.to); /* drawer stays open */ }}
                            disabled={onThisPage}
                            title={link.hint ?? (onThisPage ? 'You are already on this page' : `Go to ${link.label}`)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                              onThisPage
                                ? 'bg-gray-100 text-gray-400 cursor-default'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                          >
                            <i className={link.icon || 'ri-external-link-line'}></i>
                            <span>{link.label}</span>
                            {!onThisPage && <i className="ri-arrow-right-line text-[10px]"></i>}
                            {onThisPage && <span className="text-[10px] opacity-80">· here</span>}
                          </button>
                        );
                      })}
                      <span className="text-[10px] text-gray-400 self-center">Opens the page in the background; help stays put.</span>
                    </div>
                  )}
                  {activeTopic.launchForms && activeTopic.launchForms.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {activeTopic.launchForms.map((lf) => (
                        <button
                          key={lf.formKey}
                          type="button"
                          onClick={() => setLaunchedForm(lf.formKey)}
                          title={lf.hint ?? `Launch ${lf.label} on top of help`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                        >
                          <i className={lf.icon || 'ri-add-line'}></i>
                          <span>{lf.label}</span>
                        </button>
                      ))}
                      <span className="text-[10px] text-gray-400 self-center">Form opens on top of help — close it to return.</span>
                    </div>
                  )}
                </header>

                <div className="space-y-6">
                  {activeTopic.sections.map((section) => (
                    <section key={section.id} data-anchor={section.id} id={`help-${activeTopic.id}-${section.id}`}>
                      <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-2 flex items-center gap-2">
                        <span className="w-1 h-4 bg-indigo-500 rounded"></span>
                        {section.title}
                      </h2>
                      <div className="pl-3">{section.body}</div>
                    </section>
                  ))}
                </div>

                {activeTopic.related && activeTopic.related.length > 0 && (
                  <footer className="mt-8 pt-4 border-t">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Related</h3>
                    <ul className="flex flex-wrap gap-2">
                      {activeTopic.related.map((rid) => {
                        const rt = HELP_TOPIC_INDEX[rid];
                        if (!rt) return null;
                        return (
                          <li key={rid}>
                            <button
                              onClick={() => setTopic(rid)}
                              className="px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs hover:bg-indigo-100 flex items-center gap-1"
                            >
                              <i className={rt.icon || 'ri-file-text-line'}></i>
                              {rt.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </footer>
                )}
              </article>
            )}
          </main>
        </div>
      </div>
    </div>
    )}

    {/* Forms launched from help — rendered outside the drawer so they sit above it. */}
    <CreateWorkForm
      isOpen={launchedForm === 'CreateWorkForm'}
      onClose={closeLaunchedForm}
      onSuccess={closeLaunchedForm}
    />
    <ProposalForm
      isOpen={launchedForm === 'ProposalForm'}
      onClose={closeLaunchedForm}
      onSuccess={closeLaunchedForm}
    />
    <EstimateForm
      isOpen={launchedForm === 'EstimateForm'}
      onClose={closeLaunchedForm}
      onSuccess={closeLaunchedForm}
    />
    <DesignForm
      isOpen={launchedForm === 'DesignForm'}
      onClose={closeLaunchedForm}
      onSuccess={closeLaunchedForm}
    />
    <AdminSanctionForm
      isOpen={launchedForm === 'AdminSanctionForm'}
      onClose={closeLaunchedForm}
      onSuccess={closeLaunchedForm}
    />
    <ContractorForm
      isOpen={launchedForm === 'ContractorForm'}
      onClose={closeLaunchedForm}
      onSuccess={closeLaunchedForm}
    />
    </>,
    document.body
  );
}
